import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createUser, getCreateUserForm } from '../../../models/userModel';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import SearchableSelect from '../../../components/common/StyledSelect';
import api from '../../../services/api';

const schema = yup.object().shape({
  full_name: yup.string().required('Full Name is required'),
  email: yup.string().email('Invalid email format').required('Email is required'),
  phone: yup.string().nullable(),
  role: yup.number().required('Role is required').positive().integer(),
  department_id: yup.number().required('Department is required').positive().integer(),
  division_department: yup.number().required('Division is required').positive().integer(),
  password: yup.string().required('Password is required for new users'),
  user_procurement_otp: yup.boolean(),
  it_support: yup.boolean(),
  supervisor: yup.boolean(),
  manager: yup.boolean(),
  hod: yup.boolean(),
  director: yup.boolean(),
});

function UserCreate() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [actualDepartments, setActualDepartments] = useState([]);
  const [allDivisions, setAllDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      full_name: '',
      email: '',
      phone: '',
      role: null,
      department_id: null,
      division_department: null,
      password: '',
      user_procurement_otp: false,
      it_support: false,
      supervisor: false,
      manager: false,
      hod: false,
      director: false,
    }
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getCreateUserForm();
        if (response.status === 1 && response.data) {
          setRoles(response.data.roles || []);
          const divisions = response.data.departments || []; // These are actually divisions
          console.log('Divisions Data:', divisions);
          setAllDivisions(divisions);
          
          // Fetch actual departments
          try {
            const deptResponse = await api.get('/api/departments/categories');
            console.log('Full API Response:', deptResponse);
            if (deptResponse.data.status === 1 && deptResponse.data.data) {
              const departmentsData = deptResponse.data.data.data || deptResponse.data.data;
              console.log('Extracted Departments Data:', departmentsData);
              setActualDepartments(departmentsData);
            }
          } catch (error) {
            console.error('Error fetching departments:', error);
          }
        } else {
          toast.error("Failed to load form data.");
        }
      } catch (error) {
        console.error('Error fetching form data:', error);
        toast.error("Failed to load necessary data for the form.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      
      // Transform data to match backend expectations
      const submitData = {
        ...data,
        department: data.division_department, // Backend expects 'department' to be the division ID
        department_id: data.department_id // Actual department category ID
      };
      
      // Remove the fields we transformed to avoid confusion
      delete submitData.division_department;
      
      const response = await createUser(submitData);
      if (response.status === 1) {
        toast.success('User created successfully!');
        navigate('..', { state: { fromForm: true } });
      } else {
        toast.error(response.message || 'Failed to create user.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'An error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Prepare options for dropdowns
  const roleOptions = roles.map(r => ({ value: r.role_id, label: r.role_name }));
  const departmentOptions = Array.isArray(actualDepartments) ? actualDepartments.map(d => ({ 
    value: d.category_id, 
    label: d.category_name 
  })) : [];
  console.log('Department options:', departmentOptions);
  const divisionOptions = Array.isArray(allDivisions) ? allDivisions.map(d => ({ 
    value: d.department_id, 
    label: d.department_name 
  })) : [];

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex justify-center items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading form...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#0087D4] hover:text-blue-600 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Create New User</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: User Settings */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">User Settings</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                    Role *
                  </label>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        options={roleOptions}
                        value={field.value}
                        onChange={option => field.onChange(option?.value || null)}
                        placeholder="Select a role"
                      />
                    )}
                  />
                  {errors.role && <p className="text-red-600 text-xs mt-1">{errors.role.message}</p>}
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Permissions</h3>
              <div className="space-y-3">
                <Controller
                  name="user_procurement_otp"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="rounded border-gray-300 text-[#0087D4] focus:ring-[#0087D4]"
                      />
                      <span className="ml-2 text-sm text-gray-700">Procurement OTP</span>
                    </label>
                  )}
                />
                <Controller
                  name="it_support"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="rounded border-gray-300 text-[#0087D4] focus:ring-[#0087D4]"
                      />
                      <span className="ml-2 text-sm text-gray-700">IT Support</span>
                    </label>
                  )}
                />
                <Controller
                  name="supervisor"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="rounded border-gray-300 text-[#0087D4] focus:ring-[#0087D4]"
                      />
                      <span className="ml-2 text-sm text-gray-700">Supervisor</span>
                    </label>
                  )}
                />
                <Controller
                  name="manager"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="rounded border-gray-300 text-[#0087D4] focus:ring-[#0087D4]"
                      />
                      <span className="ml-2 text-sm text-gray-700">Manager</span>
                    </label>
                  )}
                />
                <Controller
                  name="hod"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="rounded border-gray-300 text-[#0087D4] focus:ring-[#0087D4]"
                      />
                      <span className="ml-2 text-sm text-gray-700">HOD</span>
                    </label>
                  )}
                />
                <Controller
                  name="director"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="rounded border-gray-300 text-[#0087D4] focus:ring-[#0087D4]"
                      />
                      <span className="ml-2 text-sm text-gray-700">Director</span>
                    </label>
                  )}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              <div className="flex flex-col gap-3">
                <button 
                  type="submit" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0087D4] hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : 'Create User'}
                </button>
                <button 
                  type="button" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: User Information */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">User Information</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                    Full Name *
                  </label>
                  <Controller
                    name="full_name"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="text"
                        id="full_name"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                        placeholder="Enter full name"
                        {...field}
                      />
                    )}
                  />
                  {errors.full_name && <p className="text-red-600 text-xs mt-1">{errors.full_name.message}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address *
                  </label>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="email"
                        id="email"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                        placeholder="Enter email address"
                        {...field}
                      />
                    )}
                  />
                  {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="tel"
                        id="phone"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                        placeholder="Enter phone number"
                        {...field}
                      />
                    )}
                  />
                  {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone.message}</p>}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password *
                  </label>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="password"
                        id="password"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                        placeholder="Enter password"
                        {...field}
                      />
                    )}
                  />
                  {errors.password && <p className="text-red-600 text-xs mt-1">{errors.password.message}</p>}
                </div>
              </div>
            </div>

            {/* Department & Division Card */}
            <div className="bg-white p-6 rounded-lg shadow border mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Department & Division</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="department_id" className="block text-sm font-medium text-gray-700">
                    Department *
                  </label>
                  <Controller
                    name="department_id"
                    control={control}
                    render={({ field }) => {
                      console.log('Department field value:', field.value);
                      console.log('Department options available:', departmentOptions.length);
                      return (
                        <SearchableSelect
                          options={departmentOptions}
                          value={field.value}
                          onChange={option => field.onChange(option?.value || null)}
                          placeholder="Select a department"
                        />
                      );
                    }}
                  />
                  {errors.department_id && <p className="text-red-600 text-xs mt-1">{errors.department_id.message}</p>}
                </div>

                <div>
                  <label htmlFor="division_department" className="block text-sm font-medium text-gray-700">
                    Division *
                  </label>
                  <Controller
                    name="division_department"
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        options={divisionOptions}
                        value={field.value}
                        onChange={option => field.onChange(option?.value || null)}
                        placeholder="Select a division"
                      />
                    )}
                  />
                  {errors.division_department && <p className="text-red-600 text-xs mt-1">{errors.division_department.message}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default UserCreate;
